/**
 * RouteContext - React context for tracking current route and document title.
 *
 * Provides current path, editor/document page flags, and document title setter.
 * Used for page title updates and route-aware UI logic.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface RouteContextType {
  currentPath: string;
  isOnEditorPage: boolean;
  isOnDocumentPage: boolean;
  isOnPDFPage: boolean;
  documentTitle?: string;
  setDocumentTitle: (title: string | undefined) => void;
}

const RouteContext = createContext<RouteContextType | undefined>(undefined);

export const useRouteContext = () => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error("useRouteContext must be used within a RouteContextProvider");
  }
  return context;
};

export const RouteContextProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [documentTitle, setDocumentTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  const isOnDocumentPage =
    currentPath.startsWith("/document/") ||
    currentPath.startsWith("/documents/import");
  const isOnPDFPage = currentPath.startsWith("/pdf/");
  const isOnEditorPage = isOnDocumentPage;

  const value: RouteContextType = {
    currentPath,
    isOnEditorPage,
    isOnDocumentPage,
    isOnPDFPage,
    documentTitle,
    setDocumentTitle,
  };

  return (
    <RouteContext.Provider value={value}>
      {children}
    </RouteContext.Provider>
  );
}; 